/**
 * "What's new" (ruling of 17 Oct 2026): one entry per release that went live on PRD, newest first. The notes ship with the code
 * of their release, so this file IS the history (git keeps every change to it). No database table.
 *
 * Writing rules, checked by releases.test.ts:
 * - `batch` is the release number. It is also the "last seen" version stored per person (guide record `release-notes`), so it
 *   only ever grows.
 * - `goLive` is the PRD go-live date (YYYY-MM-DD, Bangkok). A published entry must have one, with one exception: the newest
 *   published entry may hold GO_LIVE_PENDING while its release is on UAT ("date pending PRD"; no date is shown). The PRD release
 *   replaces it with the real date, and `GIRAFFE_RELEASE_TARGET=prd npx vitest run releases` fails until it does.
 * - `headline` is one line; `bullets` are 3 to 8 short lines saying what people can now do or what changed for them. Thai and
 *   English both. No technical terms, no commit ids, no people's names.
 * - `closed` lists the GRF feedback items the release closed, as keys (GRF-<n>); the screens link each to its item.
 * - `status: "unreleased"` keeps an entry out of every screen. The next release's entry waits here as a placeholder; it is
 *   filled in and set to "published" in the release itself, before it goes to UAT.
 *
 * Thai by product decision (ruling of 17 Oct 2026; since the ruling of 18 Oct 2026 the screens show the Thai text to
 * everyone, and `en` stays the record): listed in ALLOWED in test/noThaiLiterals.test.ts, and the screens carry `data-chrome-exempt="release-notes"`.
 *
 * Backfill (17 Oct 2026) from the batch and PRD release records. Go-live dates are the days the PRD release checks passed.
 * The facts the records left open were settled by the rulings of 18 Oct 2026: batch 20 closed no GRF item; GRF-21 is listed under
 * batch 14, following its closing record; GRF-24 and GRF-32 are listed under batch 16.
 */

export interface ReleaseText {
  en: string;
  th: string;
}

export interface ReleaseNote {
  batch: number;
  status: "published" | "unreleased";
  /** PRD go-live date, YYYY-MM-DD. Null while unreleased, or GO_LIVE_PENDING (also null) while a published release is on UAT. */
  goLive: string | null;
  headline: ReleaseText;
  bullets: readonly ReleaseText[];
  /** GRF keys closed by this release. */
  closed: readonly string[];
}

/**
 * RELEASE KIT MARKER. The go-live date of a release that is published but not yet on PRD: no date is shown on screen. Only the
 * newest published entry may hold it (releases.test.ts). The UAT release kit accepts it as "date pending PRD"; the PRD release
 * sets the real date in its place.
 */
export const GO_LIVE_PENDING = null;

export const RELEASES: readonly ReleaseNote[] = [
  {
    batch: 31,
    status: "published",
    goLive: GO_LIVE_PENDING,
    headline: {
      en: "Items open as a pop-up, files in comments, a date range on the CM report, and many fixes",
      th: "เปิดงานเป็นหน้าต่างป๊อปอัป แนบไฟล์ในความเห็นได้ เลือกช่วงวันที่ในรายงาน CM และแก้ไขอีกหลายจุด",
    },
    bullets: [
      {
        en: "On the Items list an item opens as a pop-up over the list, as on the Board. Its buttons sit on their own line under the title, so a long title reads in full.",
        th: "ในรายการ Items งานจะเปิดเป็นหน้าต่างป๊อปอัปทับรายการเหมือนใน Board ปุ่มต่าง ๆ อยู่บรรทัดของตัวเองใต้ชื่องาน ชื่อยาวจึงอ่านได้ครบ",
      },
      {
        en: "Attach Word, PDF and other files from the comment box with the new File button. The file also appears on the Attachments tab.",
        th: "แนบไฟล์ Word, PDF และไฟล์อื่น ๆ จากช่องความเห็นได้ด้วยปุ่ม File ใหม่ และไฟล์จะแสดงในแท็บ Attachments ด้วย",
      },
      {
        en: "Add child now takes pictures. Paste or insert them; they are attached to the new child when you save it.",
        th: "หน้าต่าง Add child ใส่รูปได้แล้ว วางหรือแทรกรูปได้ และรูปจะแนบกับงานลูกเมื่อกดบันทึก",
      },
      {
        en: "The wiki editor has a Markdown button to see and edit the page's source text.",
        th: "หน้าแก้ไข Wiki มีปุ่ม Markdown สำหรับดูและแก้ไขข้อความต้นฉบับของหน้า",
      },
      {
        en: "CM report: choose a From–To range of reported dates. The ticket tables scroll sideways, and the Word file follows the report template, with Thai text marked as Thai.",
        th: "รายงาน CM: เลือกช่วงวันที่แจ้งปัญหาแบบ จาก–ถึง ได้ ตารางรายการเลื่อนซ้ายขวาได้ และไฟล์ Word ตรงตามแม่แบบรายงาน โดยข้อความภาษาไทยถูกระบุเป็นภาษาไทย",
      },
      {
        en: "On the Board, pick several people at once to see the cards of anyone you picked.",
        th: "ใน Board เลือกหลายคนพร้อมกันได้ เพื่อดูการ์ดของทุกคนที่เลือก",
      },
      {
        en: "A story that has not started can be cancelled by the person who reported it or by the PM. On a cancel request, the reason shows next to Approve.",
        th: "Story ที่ยังไม่เริ่มยกเลิกได้โดยผู้แจ้งหรือ PM และเมื่อมีคำขอยกเลิก เหตุผลจะแสดงข้างปุ่ม Approve",
      },
      {
        en: "Fixes: wide tables in descriptions scroll; Updated sorts on Issues and actions; a required choice with no options no longer blocks a move; the phone remote counts slides like the desktop; the pointer leaves the screen when control is taken over.",
        th: "แก้ไข: ตารางกว้างในคำอธิบายเลื่อนได้ เรียงตาม Updated ในหน้า Issues and actions ได้ ช่องบังคับเลือกที่ไม่มีตัวเลือกไม่ขวางการเปลี่ยนสถานะอีก รีโมตบนมือถือนับสไลด์แบบเดียวกับคอมพิวเตอร์ และจุดชี้หายจากจอเมื่อมีการรับช่วงควบคุม",
      },
    ],
    closed: ["GRF-91", "GRF-92", "GRF-93", "GRF-94", "GRF-95", "GRF-96", "GRF-97", "GRF-98", "GRF-101", "GRF-102", "GRF-103", "GRF-104", "GRF-105", "GRF-106", "GRF-108", "GRF-109", "GRF-110"],
  },
  {
    batch: 29,
    status: "published",
    goLive: "2026-10-30",
    headline: {
      en: "Presentations: present from Giraffe to up to four screens",
      th: "งานนำเสนอ (Presentations): นำเสนอจาก Giraffe ไปยังจอได้สูงสุด 4 จอ",
    },
    bullets: [
      {
        en: "Each project has \"Presentations\". Upload a presentation as a .zip file; Giraffe sends it in small parts and shows the progress.",
        th: "ทุกโครงการมีเมนู \"Presentations\" อัปโหลดงานนำเสนอเป็นไฟล์ .zip ระบบส่งไฟล์เป็นส่วนย่อยและแสดงความคืบหน้าให้เห็น",
      },
      {
        en: "Upload a new version at any time, and make an earlier one current again from \"Versions\".",
        th: "อัปโหลดเวอร์ชันใหม่ได้ทุกเมื่อ และกลับไปใช้เวอร์ชันก่อนหน้าได้จาก \"Versions\"",
      },
      {
        en: "\"Present\" opens the controller: the current page, the next page, your notes, the page list, a timer, and Previous and Next.",
        th: "ปุ่ม \"Present\" เปิดหน้าควบคุม แสดงหน้าปัจจุบัน หน้าถัดไป โน้ตของผู้นำเสนอ รายการหน้า ตัวจับเวลา และปุ่ม Previous กับ Next",
      },
      {
        en: "On the presentation laptop, open Giraffe's /present page. It shows a 6-digit code; type it under \"Add screen\" on the controller.",
        th: "ที่เครื่องที่ต่อจอนำเสนอ เปิดหน้า /present ของ Giraffe จะเห็นรหัส 6 หลัก แล้วพิมพ์รหัสนั้นที่ \"Add screen\" บนหน้าควบคุม",
      },
      {
        en: "Pages marked for the presenter only never reach the screens; they keep showing the last page the audience may see.",
        th: "หน้าที่กำหนดให้ผู้นำเสนอดูคนเดียวจะไม่ขึ้นบนจอ จอจะค้างหน้าล่าสุดที่ผู้ชมดูได้",
      },
      {
        en: "Only members of the project see its presentations. Partners do not.",
        th: "เฉพาะสมาชิกโครงการเท่านั้นที่เห็นงานนำเสนอของโครงการ พาร์ทเนอร์ (Partner) ไม่เห็น",
      },
    ],
    closed: [],
  },
  {
    batch: 28,
    status: "published",
    goLive: "2026-10-28",
    headline: {
      en: "A wiki for every project, and a Company wiki",
      th: "วิกิสำหรับทุกโครงการ และวิกิของบริษัท (Company wiki)",
    },
    bullets: [
      {
        en: "Each project now has its own wiki, and there is one Company wiki for the whole company.",
        th: "ทุกโครงการมีวิกิของตัวเองแล้ว และมีวิกิของบริษัท (Company wiki) อีกหนึ่งแห่งสำหรับทั้งบริษัท",
      },
      {
        en: "A page starts as a draft; others see it once you \"Publish…\" it. Every published version is kept.",
        th: "หน้าใหม่เริ่มเป็นฉบับร่าง (Draft) คนอื่นจะเห็นเมื่อกด \"Publish…\" แล้ว และระบบเก็บทุกเวอร์ชันที่เผยแพร่ไว้",
      },
      {
        en: "\"Import Markdown…\" brings in Markdown files, a whole folder or a zip file as draft pages. Older files saved in a Thai encoding are converted so they read correctly.",
        th: "\"Import Markdown…\" นำเข้าไฟล์ Markdown ได้ทั้งไฟล์เดี่ยว ทั้งโฟลเดอร์ หรือไฟล์ zip เป็นหน้าฉบับร่าง ไฟล์เก่าที่บันทึกด้วยรหัสภาษาไทยแบบเดิมจะถูกแปลงให้อ่านได้ถูกต้อง",
      },
      { en: "You can attach files to a page.", th: "แนบไฟล์ไว้กับหน้าได้" },
      {
        en: "Pages can be linked to items, and you can make a new page straight from an item.",
        th: "เชื่อมหน้ากับงานได้ และสร้างหน้าใหม่จากงานได้โดยตรง",
      },
      {
        en: "A page can be restricted to named people or groups. Nobody else sees it, SystemAdmin included.",
        th: "จำกัดให้หน้าใดหน้าหนึ่งเห็นได้เฉพาะคนหรือกลุ่มที่ระบุ คนอื่นจะไม่เห็นหน้านั้น รวมถึง SystemAdmin ด้วย",
      },
      { en: "Partners do not see the wiki.", th: "พาร์ทเนอร์ (Partner) ไม่เห็นวิกิ" },
    ],
    closed: [],
  },
  {
    batch: 27,
    status: "published",
    goLive: "2026-10-24",
    headline: {
      en: "Weekly status, round 2: contributors, comments, RAG decisions and reminders",
      th: "หน้ารายงานสถานะรายสัปดาห์รอบ 2: ผู้ร่วมรายงาน ความเห็น การตัดสิน RAG และการแจ้งเตือน",
    },
    bullets: [
      {
        en: "Report by Thursday 23:59 (\"Report by …, 23:59\"). At Saturday 00:00 Giraffe saves and locks that week's report by itself, and starts the next week from it.",
        th: "รายงานให้เสร็จภายในวันพฤหัสบดี 23:59 (หน้าจอแสดง \"Report by …, 23:59\") วันเสาร์ 00:00 ระบบเก็บสำเนารายงานของสัปดาห์นั้นและล็อกไว้เอง แก้ไขไม่ได้อีก และเปิดสัปดาห์ใหม่โดยตั้งต้นจากของสัปดาห์ก่อน",
      },
      {
        en: "On Wednesday and Thursday at 09:00, the Capt. of a project whose week is not marked gets a bell reminder, and a Telegram one if linked. Telegram can be turned off under \"Weekly status reminders (Wed and Thu 09:00)\".",
        th: "ทุกวันพุธและวันพฤหัสบดี 09:00 Capt. ของโครงการที่ยังไม่ได้ Mark สัปดาห์นี้จะได้รับแจ้งเตือนที่กระดิ่งเสมอ และทาง Telegram หากเชื่อมไว้ ปิดเฉพาะ Telegram ได้ที่ \"Weekly status reminders (Wed and Thu 09:00)\"",
      },
      {
        en: "The Capt. or the Chief Pilot can add project members as \"Status contributors\". They see the page, add and edit any row, and delete only rows they added. Rows save one at a time; on a clash your text is kept and you choose \"Keep mine\" or \"Use theirs\".",
        th: "Capt. หรือ Chief Pilot เพิ่ม \"Status contributors\" จากสมาชิกโครงการได้ ผู้ร่วมรายงานเห็นหน้านี้ เพิ่มและแก้ไขแถวได้ทุกแถว แต่ลบได้เฉพาะแถวที่ตนเพิ่ม บันทึกทีละแถว หากมีคนบันทึกชนกัน ข้อความของคุณยังอยู่ ให้เลือก \"Keep mine\" หรือ \"Use theirs\"",
      },
      {
        en: "\"Comments and history\" takes comments on the whole report or on one row or field, with @ mentions. Threads are Open, Answered or Resolved, and unresolved ones carry into next week. The thread's author, the Capt. and the Chief Pilot can \"Resolve\" (the Chief Pilot also others' threads). Comments never change the report.",
        th: "แผง \"Comments and history\" ใช้แสดงความเห็นต่อทั้งรายงาน หรือต่อแถว/ช่องใดช่องหนึ่ง และใช้ @ เพื่อกล่าวถึงคนได้ เธรดมีสถานะ Open, Answered, Resolved และเธรดที่ยังไม่ปิดจะตามไปสัปดาห์ถัดไป ผู้เริ่มเธรด Capt. และ Chief Pilot กด \"Resolve\" ได้ (Chief Pilot ปิดเธรดของคนอื่นได้ด้วย) ความเห็นไม่เปลี่ยนเนื้อหารายงาน",
      },
      {
        en: "The Capt. proposes the RAG (\"Proposed RAG\"); the Chief Pilot decides it (\"Decided RAG\"): Green · On track / Amber · At risk / Red · Off track. A reason is needed when it is not Green or changed from last week; last week's reason counts only while the RAG is unchanged. \"Since last week\" compares with last week's decided RAG, e.g. \"RAG: Red (decided) → Amber (proposed)\".",
        th: "Capt. เสนอ \"Proposed RAG\" Chief Pilot ตัดสิน \"Decided RAG\" (Green · On track / Amber · At risk / Red · Off track) ต้องใส่เหตุผลเมื่อไม่ใช่ Green หรือเปลี่ยนจากสัปดาห์ก่อน เหตุผลเดิมใช้ต่อได้เฉพาะเมื่อ RAG ไม่เปลี่ยน \"Since last week\" เทียบกับ RAG ที่ตัดสินแล้วของสัปดาห์ก่อน เช่น \"RAG: Red (decided) → Amber (proposed)\"",
      },
      {
        en: "The PMO can now read and comment on a project's status page. It cannot edit the report.",
        th: "PMO อ่านและแสดงความเห็นในหน้ารายงานสถานะของโครงการได้แล้ว แต่แก้ไขรายงานไม่ได้",
      },
      {
        en: "Mark with no colour asks \"No colour set — Mark anyway?\". Once marked, the deadline text and the Mark buttons hide. \"No change this week\" is refused when the content differs from last week. A No change week edited afterwards shows \"Mark as reported\" again; marking it after Thursday 23:59 counts as \"Reported late\".",
        th: "ถ้ากด Mark โดยยังไม่เลือกสี ระบบจะถาม \"No colour set — Mark anyway?\" เมื่อ Mark แล้ว ข้อความกำหนดส่งและปุ่ม Mark จะซ่อน \"No change this week\" ใช้ไม่ได้ถ้าเนื้อหาต่างจากสัปดาห์ก่อน ถ้าแก้สัปดาห์ที่กด No change ไปแล้ว ปุ่ม \"Mark as reported\" จะกลับมา กด Mark อีกครั้งหลังพฤหัสบดี 23:59 นับเป็น \"Reported late\"",
      },
      {
        en: "A new per-project setting, \"Show schedule bars\" (on by default), is off for maintenance and service projects, so they have no Schedule section. History is an audit record only; \"Stop showing in report\" adds no line to the item's History.",
        th: "ตั้งค่าใหม่ต่อโครงการ \"Show schedule bars\" (เปิดไว้เป็นค่าเริ่มต้น) โครงการ MA และงานบริการปิดไว้ จึงไม่มีส่วน Schedule ส่วน History บันทึกไว้เพื่อการตรวจสอบเท่านั้น และการกด \"Stop showing in report\" ไม่เพิ่มบรรทัดใน History ของงานนั้น",
      },
    ],
    closed: [],
  },
  {
    batch: 26,
    status: "published",
    goLive: "2026-10-22",
    headline: {
      en: "A weekly project status page, a required deployment environment, and smaller fixes",
      th: "หน้ารายงานสถานะโครงการรายสัปดาห์ Environment ของ Deployment ต้องกรอก และการปรับปรุงเล็ก ๆ อื่น ๆ",
    },
    bullets: [
      {
        en: "Each project has a weekly status page: the PM reports by Thursday, with a progress table and a deck attached. Only the PM, the Chief Pilot and SystemAdmin can see it.",
        th: "แต่ละโครงการมีหน้ารายงานสถานะรายสัปดาห์ PM รายงานภายในวันพฤหัสบดี พร้อมตารางความคืบหน้าและแนบไฟล์สไลด์ โดยเห็นได้เฉพาะ PM, Chief Pilot และ SystemAdmin",
      },
      {
        en: "Environment is now required on a deployment, and Approve asks for it when it is empty.",
        th: "Environment เป็นช่องที่ต้องกรอกใน Deployment แล้ว และเมื่อกด Approve ระบบจะถามหาหากยังว่างอยู่",
      },
      { en: "The Links tab shows how many links an item has.", th: "แท็บ Links แสดงจำนวนลิงก์ของงาน" },
      {
        en: "Edit on a board card opens in the side pane, the same as viewing it.",
        th: "Edit จากการ์ดบนบอร์ดเปิดในแผงด้านข้าง เหมือนกับการดูรายละเอียด",
      },
      { en: "Incident cancel requests now appear in Needs attention.", th: "คำขอยกเลิก Incident แสดงใน Needs attention แล้ว" },
      { en: "Saved filters keep the \"Involves me\" choice.", th: "ตัวกรองที่บันทึกไว้จำตัวเลือก \"Involves me\" ไว้ด้วย" },
      { en: "Person pickers work better with screen readers.", th: "ช่องเลือกคนรองรับโปรแกรมอ่านหน้าจอได้ดีขึ้น" },
    ],
    closed: ["GRF-87", "GRF-88", "GRF-89", "GRF-90"],
  },
  {
    batch: 25,
    status: "published",
    goLive: "2026-10-20",
    headline: {
      en: "Ask to cancel an incident, pin more people, and pop-ups that stay on screen",
      th: "ขอยกเลิก Incident ได้ ปักหมุดคนได้มากขึ้น และป๊อปอัปไม่ล้นจอ",
    },
    bullets: [
      {
        en: "The SA, the service desk, the developer and the tester can choose Request Cancellation on an open incident, with a reason; the PM is told that an approval is waiting.",
        th: "SA ทีมซัพพอร์ต Developer และ Tester เลือก \"Request Cancellation\" (ขอยกเลิก) ใน Incident ที่ยังเปิดอยู่ได้ โดยระบุเหตุผล และ PM จะได้รับแจ้งว่ามีรายการรออนุมัติ",
      },
      {
        en: "The PM approves, which cancels the incident, or declines with a reason, which returns it to its step; whoever asked is told the outcome.",
        th: "PM อนุมัติเพื่อยกเลิก หรือปฏิเสธพร้อมเหตุผลซึ่ง Incident จะกลับไปที่ขั้นตอนเดิม และผู้ที่ขอจะได้รับแจ้งผล",
      },
      {
        en: "A request cannot be made while an incident is on hold, waiting on the customer, or awaiting the customer's confirmation.",
        th: "ไม่สามารถขอยกเลิกได้ระหว่างที่ Incident พักไว้ชั่วคราว รอข้อมูลจากลูกค้า หรือรอลูกค้ายืนยันผล",
      },
      {
        en: "BMS incidents now follow the standard incident steps, including the role questions and the confirmed Assignee. Reject from Assigned is no longer offered there.",
        th: "Incident ของ BMS ใช้ขั้นตอนมาตรฐานแล้ว รวมถึงการถามชื่อตามบทบาทและการยืนยันผู้รับมอบหมาย และไม่มี Reject จากสถานะ Assigned อีกต่อไป",
      },
      {
        en: "Pin up to 100 people to the board's person bar (was 10); the faces wrap onto a new line.",
        th: "ปักหมุดคนไว้หน้าแถบรายชื่อบนบอร์ดได้สูงสุด 100 คน (จากเดิม 10 คน) รูปจะขึ้นบรรทัดใหม่เมื่อเต็มแถว",
      },
      {
        en: "The board's \"+N\" people list and long pop-ups such as Edit stay on screen and scroll, so Save is always reachable.",
        th: "รายการ \"+N\" บนบอร์ดและป๊อปอัปที่ยาว เช่นหน้า Edit ไม่ล้นจอแล้ว และเลื่อนดูได้จนถึงปุ่ม Save",
      },
      {
        en: "After you set an Assignee on Edit, the item offers \"Assign and move to Assigned…\" if you may make that move; testers may now assign defects too.",
        th: "เมื่อกำหนด Assignee ในหน้า Edit แล้ว ระบบจะเสนอ \"Assign and move to Assigned…\" หากคุณมีสิทธิ์ และ Tester มอบหมายงาน Defect ได้แล้ว",
      },
      {
        en: "In person pickers, press Backspace or Delete in the empty box to clear the person, or type \"not\" to find Not named.",
        th: "ในช่องเลือกคน กด Backspace หรือ Delete ในช่องว่างเพื่อล้างชื่อ หรือพิมพ์ \"not\" เพื่อหา Not named",
      },
    ],
    closed: ["GRF-83", "GRF-84", "GRF-85", "GRF-86"],
  },
  {
    batch: 24,
    status: "published",
    goLive: "2026-10-20",
    headline: {
      en: "Moves now ask for the Developer, SA and Tester, and confirm the Assignee",
      th: "การย้ายสถานะงานจะถามชื่อ Developer, SA และ Tester และให้ยืนยันผู้รับมอบหมายงาน",
    },
    bullets: [
      {
        en: "Moving an item into a role's stage asks who the Developer, SA or Tester is. It is optional and already filled in when known.",
        th: "เมื่อย้ายงานเข้าสู่ขั้นตอนของบทบาทใด ระบบจะถามชื่อ Developer, SA หรือ Tester โดยไม่บังคับ และกรอกให้ล่วงหน้าหากทราบอยู่แล้ว",
      },
      {
        en: "Every assign move asks you to confirm exactly one Assignee, filled in with the person named for that stage.",
        th: "ทุกการมอบหมายงานจะให้ยืนยันผู้รับมอบหมายหนึ่งคน โดยกรอกชื่อผู้ที่ระบุไว้สำหรับขั้นตอนนั้นให้ล่วงหน้า",
      },
      {
        en: "Notices about a stage go to the person named for that stage, not to everyone in the role.",
        th: "การแจ้งเตือนของแต่ละขั้นตอนส่งถึงผู้ที่ระบุไว้สำหรับขั้นตอนนั้น ไม่ส่งถึงทุกคนในบทบาท",
      },
      {
        en: "Fewer notices: you are no longer told about stages that are not yours.",
        th: "การแจ้งเตือนน้อยลง ระบบจะไม่แจ้งเรื่องขั้นตอนที่ไม่ใช่ของคุณ",
      },
    ],
    closed: ["GRF-71", "GRF-74"],
  },
  {
    batch: 23,
    status: "published",
    goLive: "2026-10-19",
    headline: {
      en: "Developer, SA and Tester on every item, and the board shows who is responsible now",
      th: "ระบุ Developer, SA และ Tester ในทุกงาน และบอร์ดแสดงผู้รับผิดชอบในตอนนี้",
    },
    bullets: [
      { en: "Each item can name its Developer, SA and Tester.", th: "งานแต่ละรายการระบุ Developer, SA และ Tester ได้" },
      {
        en: "The board shows who is responsible now at each stage, marked \"as SA\" or \"as Tester\" when it is not the assignee.",
        th: "บอร์ดแสดงผู้รับผิดชอบในตอนนี้ตามขั้นตอนของงาน และบอกว่าเป็น SA หรือ Tester เมื่อไม่ใช่ผู้รับมอบหมายงาน",
      },
      { en: "Every item's details start with who is responsible now.", th: "รายละเอียดของทุกงานแสดงผู้รับผิดชอบในตอนนี้ไว้เป็นบรรทัดแรก" },
      {
        en: "The Items list has Developer, SA and Tester columns and an \"Involves me\" filter.",
        th: "หน้ารายการงานมีคอลัมน์ Developer, SA และ Tester และตัวกรอง \"Involves me\"",
      },
      {
        en: "You are told when an item reaches the stage for your role; choose it under \"My role on an item\" in notification settings.",
        th: "ระบบแจ้งเตือนเมื่องานมาถึงขั้นตอนของบทบาทคุณ เลือกได้ที่ \"My role on an item\" ในการตั้งค่าการแจ้งเตือน",
      },
      { en: "Partners also see who is responsible now on the items they can see.", th: "พาร์ทเนอร์เห็นผู้รับผิดชอบในตอนนี้ของงานที่ตนเห็นได้ด้วย" },
      { en: "Admins can see who has connected AI tools and Telegram.", th: "ผู้ดูแลระบบดูได้ว่าใครเชื่อมต่อเครื่องมือ AI และ Telegram แล้ว" },
    ],
    closed: [],
  },
  {
    batch: 22,
    status: "published",
    goLive: "2026-10-18",
    headline: {
      en: "Pin people on the board, clearer profile pictures and a What's new page",
      th: "ปักหมุดคนบนบอร์ด รูปโปรไฟล์แสดงชัดเจน และหน้ามีอะไรใหม่",
    },
    bullets: [
      { en: "Pin up to 10 people to the front of the board's person bar.", th: "ปักหมุดคนได้สูงสุด 10 คนไว้ด้านหน้าของแถบรายชื่อคนบนบอร์ด" },
      { en: "The card panel now stays in view while you scroll a long board.", th: "แผงรายละเอียดการ์ดอยู่ในจอตลอดขณะเลื่อนบอร์ดที่ยาว" },
      { en: "Send to SA… no longer appears on closed or cancelled tickets.", th: "ปุ่ม Send to SA… ไม่แสดงในงานที่ปิดหรือยกเลิกแล้ว" },
      { en: "Test level can be chosen when creating a Defect.", th: "เลือก Test level ได้ตอนสร้าง Defect" },
      { en: "Profile pictures load reliably everywhere.", th: "รูปโปรไฟล์แสดงได้ทุกที่ ไม่หายอีกแล้ว" },
      { en: "Feedback screenshots now show the workflow diagram correctly.", th: "ภาพหน้าจอที่แนบกับการแจ้งปัญหาแสดงแผนภาพขั้นตอนงานได้ถูกต้องแล้ว" },
      { en: "This What's new page, which opens once after each release.", th: "หน้ามีอะไรใหม่นี้ ซึ่งจะเปิดขึ้นครั้งเดียวหลังการอัปเดตแต่ละรุ่น" },
    ],
    closed: ["GRF-65", "GRF-80", "GRF-81"],
  },
  {
    batch: 21,
    status: "published",
    goLive: "2026-10-17",
    headline: {
      en: "Simpler closing, Test level choices, Incident cancel and easier boards",
      th: "ปิดงานง่ายขึ้น เลือก Test level ได้ ยกเลิก Incident ได้ และบอร์ดใช้งานง่ายขึ้น",
    },
    bullets: [
      { en: "The Capt. and the Chief Pilot can close an item without typing a reason.", th: "Capt. และ Chief Pilot ปิดงานได้โดยไม่ต้องพิมพ์เหตุผล" },
      {
        en: "An Incident can be cancelled with a reason, and the person who raised it is told.",
        th: "ยกเลิก Incident ได้โดยระบุเหตุผล และผู้แจ้งจะได้รับแจ้ง",
      },
      { en: "On a Defect, Test level is a choice: Unit, SIT, UAT or Production.", th: "Test level ของ Defect เป็นตัวเลือก: Unit, SIT, UAT หรือ Production" },
      {
        en: "The board's card panel is wider and has Open full page, and every column is full height, so you can drop a card anywhere.",
        th: "แผงรายละเอียดการ์ดบนบอร์ดกว้างขึ้นและมีปุ่มเปิดหน้าเต็ม ทุกคอลัมน์สูงเต็มจอ วางการ์ดตรงไหนก็ได้",
      },
      { en: "Tables in the editor: add or remove rows and columns.", th: "ตารางในช่องแก้ไขข้อความ เพิ่มหรือลบแถวและคอลัมน์ได้" },
      {
        en: "The parent line on an item is easier to see, and Severity (migrated) is no longer shown.",
        th: "บรรทัดงานแม่บนหน้างานเห็นชัดขึ้น และไม่แสดง Severity (migrated) อีกแล้ว",
      },
      {
        en: "Start Deployment can be pressed as soon as the confirmed window opens, and Cancel says it ends a deployment without deploying.",
        th: "ปุ่ม Start Deployment กดได้ทันทีเมื่อถึงช่วงเวลาที่ยืนยันไว้ และปุ่ม Cancel บอกชัดว่าเป็นการจบโดยไม่ deploy",
      },
      { en: "When your session expires, you can sign back in with a passkey.", th: "เมื่อเซสชันหมดอายุ ลงชื่อเข้าใช้อีกครั้งด้วยพาสคีย์ได้" },
    ],
    closed: ["GRF-66", "GRF-67", "GRF-68", "GRF-69", "GRF-70", "GRF-72", "GRF-73", "GRF-75", "GRF-76", "GRF-77", "GRF-78", "GRF-79"],
  },
  {
    batch: 20,
    status: "published",
    goLive: "2026-10-17",
    headline: { en: "The sidebar shows what your role uses", th: "เมนูด้านข้างแสดงสิ่งที่บทบาทของคุณใช้" },
    bullets: [
      { en: "The sidebar on each project shows the pages your role works with.", th: "เมนูด้านข้างในแต่ละโครงการแสดงหน้าที่บทบาทของคุณใช้งาน" },
      {
        en: "A System Admin can choose which roles see each sidebar entry, in Settings, Permissions.",
        th: "System Admin กำหนดได้ว่าบทบาทใดเห็นเมนูใด ที่ Settings หัวข้อ Permissions",
      },
      {
        en: "Analysts can do everything a Member can, including asking for a deployment and reviewing work.",
        th: "Analyst ทำได้ทุกอย่างที่ Member ทำได้ รวมถึงขอ deploy และตรวจทานงาน",
      },
      {
        en: "New deployment and Add selected to deployment appear only when you may ask for a deployment.",
        th: "ปุ่ม New deployment และ Add selected to deployment แสดงเฉพาะเมื่อคุณขอ deploy ได้",
      },
      { en: "Approvals appears only for people who approve deployments.", th: "เมนู Approvals แสดงเฉพาะผู้ที่อนุมัติการ deploy" },
    ],
    // No GRF item was closed by batch 20 (confirmed, ruling of 18 Oct 2026).
    closed: [],
  },
  {
    batch: 19,
    status: "published",
    goLive: "2026-10-14",
    headline: {
      en: "Item preview on lists, Events, and Resolve then Close for Service Requests",
      th: "ดูตัวอย่างงานจากหน้ารายการ อีเวนต์ และ Resolve แล้ว Close สำหรับ Service Request",
    },
    bullets: [
      {
        en: "Open a quick preview of an item beside Issues and actions, Items, Change management and Backlog.",
        th: "เปิดดูตัวอย่างงานด้านข้างได้จากหน้า Issues and actions, Items, Change management และ Backlog",
      },
      {
        en: "The service desk resolves a Service Request, then the Capt. or the Chief Pilot closes it or sends it back.",
        th: "ทีม Service desk กด Resolve ให้ Service Request แล้ว Capt. หรือ Chief Pilot เป็นผู้ปิดหรือส่งกลับ",
      },
      {
        en: "Set when a Service Request or Incident was really reported, up to 30 days back; its response time counts from then.",
        th: "ระบุเวลาที่แจ้ง Service Request หรือ Incident จริงย้อนหลังได้ไม่เกิน 30 วัน และนับเวลาตอบสนองจากเวลานั้น",
      },
      {
        en: "Events: plan a round of work with dates and lists, link items to it, filter by it and import its lists from Excel.",
        th: "อีเวนต์: วางแผนรอบงานพร้อมวันที่และรายการค่า ผูกงานกับอีเวนต์ กรองตามอีเวนต์ และนำเข้ารายการค่าจาก Excel",
      },
      { en: "Customer testers can retest defects in their own portal.", th: "ผู้ทดสอบฝั่งลูกค้าทดสอบ Defect ซ้ำได้ในพอร์ทัลของตนเอง" },
      { en: "Mentions show people's English display names, as everywhere else.", th: "การกล่าวถึงคนแสดงชื่อภาษาอังกฤษ เหมือนส่วนอื่นของระบบ" },
      { en: "Clearer words when a move or an action is not allowed.", th: "ข้อความชัดเจนขึ้นเมื่อย้ายสถานะหรือทำรายการไม่ได้" },
    ],
    closed: ["GRF-60", "GRF-61", "GRF-62", "GRF-63"],
  },
  {
    batch: 18,
    status: "published",
    goLive: "2026-10-12",
    headline: {
      en: "Passkey sign-in, project Reports and sprint retrospectives",
      th: "ลงชื่อเข้าใช้ด้วยพาสคีย์ หน้ารายงานโครงการ และการทบทวนสปรินต์",
    },
    bullets: [
      {
        en: "Sign in with a passkey (fingerprint, face or your phone) and manage your passkeys in Profile.",
        th: "ลงชื่อเข้าใช้ด้วยพาสคีย์ (ลายนิ้วมือ ใบหน้า หรือโทรศัพท์) และจัดการพาสคีย์ได้ที่ Profile",
      },
      {
        en: "A Reports page for each project: burndown, status, created against resolved, workload, RAID and SLA.",
        th: "หน้า Reports ของแต่ละโครงการ: burndown สถานะ งานที่สร้างเทียบกับงานที่ปิด ภาระงาน RAID และ SLA",
      },
      {
        en: "Sprints have a planned start with two-week defaults, and a retrospective page.",
        th: "สปรินต์มีวันเริ่มตามแผน ค่าเริ่มต้นสองสัปดาห์ และมีหน้าทบทวนสปรินต์",
      },
      { en: "A move you cannot make says why, and who can make it.", th: "การย้ายสถานะที่คุณทำไม่ได้จะบอกเหตุผล และบอกว่าใครทำได้" },
      {
        en: "Hover over or press and hold a person to see their card; item lists show people's pictures, people before AI agents.",
        th: "ชี้หรือกดค้างที่คนเพื่อดูการ์ดข้อมูล หน้ารายการงานแสดงรูปของคน และเรียงคนก่อนเอเจนต์ AI",
      },
      { en: "Projects have icons, and the project picker shows them.", th: "โครงการมีไอคอน และแสดงในตัวเลือกโครงการ" },
      { en: "The Capt. and the Chief Pilot can build quick filters on the board.", th: "Capt. และ Chief Pilot สร้างตัวกรองด่วนบนบอร์ดได้" },
      { en: "One Notification settings page, and a Mark all read you can find.", th: "รวมการตั้งค่าการแจ้งเตือนไว้หน้าเดียว และปุ่ม Mark all read หาเจอง่าย" },
    ],
    closed: ["GRF-45", "GRF-46", "GRF-47", "GRF-48", "GRF-49", "GRF-50", "GRF-51", "GRF-52", "GRF-53", "GRF-54", "GRF-55", "GRF-56", "GRF-57", "GRF-58"],
  },
  {
    batch: 17,
    status: "published",
    goLive: "2026-10-11",
    headline: {
      en: "Notification bell, Telegram, watching items and emergency codes",
      th: "กระดิ่งแจ้งเตือน Telegram การติดตามงาน และรหัสฉุกเฉิน",
    },
    bullets: [
      { en: "A bell in the header gathers your notices and what needs your attention.", th: "กระดิ่งที่แถบด้านบนรวมการแจ้งเตือนและเรื่องที่รอคุณไว้ที่เดียว" },
      {
        en: "Watch an item to hear about its changes; you already watch items you raised, were given, commented on or were mentioned in.",
        th: "กดติดตามงานเพื่อรับแจ้งเมื่อมีการเปลี่ยนแปลง และระบบติดตามให้แล้วสำหรับงานที่คุณแจ้ง รับผิดชอบ แสดงความเห็น หรือถูกกล่าวถึง",
      },
      { en: "Get your notices on Telegram: link it from your Profile.", th: "รับการแจ้งเตือนทาง Telegram ได้ โดยเชื่อมบัญชีที่ Profile" },
      {
        en: "In an emergency, ask for a one-time code that lets urgent work skip the usual steps; every use is reported.",
        th: "กรณีฉุกเฉิน ขอรหัสใช้ครั้งเดียวเพื่อให้งานเร่งด่วนข้ามขั้นตอนปกติได้ และทุกครั้งที่ใช้จะถูกรายงาน",
      },
      {
        en: "Link, parent and scope fields accept just a number or a pasted Giraffe address.",
        th: "ช่องลิงก์ งานแม่ และขอบเขต ใส่แค่ตัวเลขหรือวางที่อยู่หน้า Giraffe ได้",
      },
      {
        en: "Drag the board's preview panel wider (it remembers), and drag to resize pictures in the editor.",
        th: "ลากขยายแผงตัวอย่างบนบอร์ดได้และระบบจำขนาดไว้ และลากปรับขนาดรูปในช่องแก้ไขข้อความได้",
      },
      {
        en: "Deployments: newest first in the backlog, a Description, a downtime hint, and blocked reasons that take you to what to fix.",
        th: "การ deploy: backlog เรียงใหม่สุดก่อน มีช่อง Description มีคำแนะนำเรื่อง downtime และเหตุผลที่ติดอยู่พาไปยังจุดที่ต้องแก้",
      },
      {
        en: "Create from a sprint board picks its active sprint, a linked dev card can be made from a Service Request or Incident, and .md files open for reading.",
        th: "สร้างงานจากบอร์ดสปรินต์จะเลือกสปรินต์ปัจจุบันให้ สร้างการ์ดงานพัฒนาจาก Service Request หรือ Incident ได้ และเปิดอ่านไฟล์ .md ได้ในหน้า",
      },
    ],
    closed: ["GRF-31", "GRF-33", "GRF-34", "GRF-35", "GRF-36", "GRF-37", "GRF-38", "GRF-39", "GRF-40", "GRF-41", "GRF-42", "GRF-43", "GRF-44"],
  },
  {
    batch: 16,
    status: "published",
    goLive: "2026-10-09",
    headline: {
      en: "Search by number, Copy link, Excel export and a stronger Items list",
      th: "ค้นหาด้วยตัวเลข คัดลอกลิงก์ ส่งออก Excel และหน้า Items ที่ทำได้มากขึ้น",
    },
    bullets: [
      {
        en: "Type a key, a global ID or just the number in the search box to open an item.",
        th: "พิมพ์รหัสงาน global ID หรือแค่ตัวเลขในช่องค้นหาเพื่อเปิดงาน",
      },
      { en: "Copy an item's link with one click beside its ID.", th: "คัดลอกลิงก์งานได้ในคลิกเดียวที่ข้างรหัสงาน" },
      {
        en: "Items list: quick filters, more filters, saved filters, a column chooser, page size, and several statuses at once (on the board too).",
        th: "หน้า Items: ตัวกรองด่วน ตัวกรองเพิ่มเติม บันทึกตัวกรอง เลือกคอลัมน์ จำนวนต่อหน้า และเลือกหลายสถานะพร้อมกัน (บนบอร์ดด้วย)",
      },
      { en: "Export the Items list to Excel exactly as you see it.", th: "ส่งออกหน้า Items เป็น Excel ตามที่เห็นบนจอ" },
      {
        en: "Pictures in descriptions and comments show small; click one to see it in full.",
        th: "รูปในรายละเอียดและความเห็นแสดงเป็นภาพย่อ คลิกเพื่อดูภาพเต็ม",
      },
      { en: "The uploader or the Capt. can delete an attachment, and it can be brought back.", th: "ผู้อัปโหลดหรือ Capt. ลบไฟล์แนบได้ และกู้คืนได้" },
      {
        en: "Chief Pilot, Capt., FO and Analyst can set the sprint when creating or editing an item.",
        th: "Chief Pilot, Capt., FO และ Analyst กำหนดสปรินต์ได้ตอนสร้างหรือแก้ไขงาน",
      },
      {
        en: "Test level is optional when you mark a defect as tested, and saving a person's project roles works again.",
        th: "ไม่ต้องระบุ Test level ตอนกด Mark as Tested และบันทึกบทบาทในโครงการของผู้ใช้ได้ตามปกติแล้ว",
      },
    ],
    // GRF-24 (code fix shipped here after the 8 Oct data fix) and GRF-32 (search by number) belong here (ruling of 18 Oct 2026).
    closed: ["GRF-20", "GRF-22", "GRF-23", "GRF-24", "GRF-25", "GRF-27", "GRF-28", "GRF-29", "GRF-30", "GRF-32"],
  },
  {
    batch: 15,
    status: "published",
    goLive: "2026-10-08",
    headline: { en: "AI assistants can help with work items", th: "ผู้ช่วย AI ช่วยทำงานกับรายการงานได้" },
    bullets: [
      {
        en: "An AI assistant connected to Giraffe can create and edit items, add comments and move items, under the same rules as you.",
        th: "ผู้ช่วย AI ที่เชื่อมต่อกับ Giraffe สร้างและแก้ไขงาน เพิ่มความเห็น และย้ายสถานะงานได้ ภายใต้กฎเดียวกับคุณ",
      },
      { en: "Everything an AI assistant writes is marked AI.", th: "ทุกอย่างที่ผู้ช่วย AI เขียนจะมีป้าย AI กำกับ" },
      { en: "Access tokens now work only for connecting an AI assistant.", th: "Access token ใช้ได้เฉพาะการเชื่อมต่อผู้ช่วย AI เท่านั้น" },
      {
        en: "ATC (Admin) is no longer offered pictures in places where it cannot add them.",
        th: "ATC (Admin) จะไม่เห็นตัวเลือกใส่รูปในที่ที่ใส่ไม่ได้อีก",
      },
    ],
    closed: ["GRF-15"],
  },
  {
    batch: 14,
    status: "published",
    goLive: "2026-10-08",
    headline: {
      en: "Work type icons, filtering by people, and pictures on Create",
      th: "ไอคอนประเภทงาน การกรองตามคน และใส่รูปตอนสร้างงาน",
    },
    bullets: [
      { en: "Work types show as coloured icons.", th: "ประเภทงานแสดงเป็นไอคอนสี" },
      {
        en: "Filter a board or the Items list by clicking people's pictures, by assignee or by reporter; Giraffe remembers your choice.",
        th: "กรองบอร์ดหรือหน้า Items ด้วยการคลิกรูปคน ตามผู้รับผิดชอบหรือผู้แจ้ง และระบบจำตัวเลือกไว้",
      },
      { en: "Insert, paste or drop pictures while creating an item.", th: "แทรก วาง หรือลากรูปใส่ได้ตอนสร้างงาน" },
      { en: "A file that is too big is refused with a clear message.", th: "ไฟล์ที่ใหญ่เกินจะแจ้งเป็นข้อความที่เข้าใจง่าย" },
      {
        en: "Roles show their crew names (Chief Pilot, Capt., FO and the others) everywhere.",
        th: "บทบาทแสดงเป็นชื่อลูกเรือ (Chief Pilot, Capt., FO และอื่น ๆ) ทุกที่",
      },
      { en: "Boards can group cards into lanes by assignee, epic or story.", th: "บอร์ดจัดกลุ่มการ์ดเป็นแถวตามผู้รับผิดชอบ epic หรือ story ได้" },
      { en: "AI co-pilot accounts have a coloured ring around their picture.", th: "บัญชีผู้ช่วย AI มีวงแหวนสีรอบรูปโปรไฟล์" },
    ],
    // GRF-21 (image preview in Attachments) is listed here, following its closing record (ruling of 18 Oct 2026).
    closed: ["GRF-3", "GRF-4", "GRF-5", "GRF-7", "GRF-10", "GRF-18", "GRF-21"],
  },
  {
    batch: 13,
    status: "published",
    goLive: "2026-10-08",
    headline: {
      en: "Easier people pickers, more Items list columns and board fixes",
      th: "เลือกคนง่ายขึ้น คอลัมน์ในหน้า Items มากขึ้น และแก้ไขบอร์ด",
    },
    bullets: [
      {
        en: "Find a person by typing part of their name; only the project's members are offered.",
        th: "ค้นหาคนด้วยการพิมพ์บางส่วนของชื่อ และแสดงเฉพาะสมาชิกของโครงการ",
      },
      { en: "Assign to me tells you why when you are not a member of the project.", th: "ปุ่ม Assign to me บอกเหตุผลเมื่อคุณไม่ได้เป็นสมาชิกของโครงการ" },
      {
        en: "The Items list can show and filter by Reporter, Priority, Sprint and Created date.",
        th: "หน้า Items แสดงและกรองตามผู้แจ้ง ความสำคัญ สปรินต์ และวันที่สร้างได้",
      },
      { en: "Change the assignee straight from the Items list.", th: "เปลี่ยนผู้รับผิดชอบได้จากหน้า Items โดยตรง" },
      {
        en: "Back returns the board and the backlog to the same filters and scroll position.",
        th: "ปุ่มย้อนกลับพาบอร์ดและแบ็กล็อกกลับไปที่ตัวกรองและตำแหน่งเดิม",
      },
      {
        en: "Closed cards sit in Done, and each status appears in one board column only.",
        th: "การ์ดที่ปิดแล้วอยู่ในคอลัมน์ Done และแต่ละสถานะอยู่ในคอลัมน์เดียวบนบอร์ด",
      },
      {
        en: "Pictures open in a preview inside Giraffe, and pictures brought over from Jira show in place.",
        th: "รูปเปิดดูได้ภายใน Giraffe และรูปที่ย้ายมาจาก Jira แสดงในตำแหน่งเดิม",
      },
      { en: "Changes made by an AI assistant are marked AI in History and comments.", th: "การเปลี่ยนแปลงที่ทำโดยผู้ช่วย AI มีป้าย AI ในประวัติและความเห็น" },
    ],
    closed: ["GRF-2", "GRF-6", "GRF-8", "GRF-9", "GRF-11", "GRF-12", "GRF-13", "GRF-14", "GRF-16", "GRF-19"],
  },
];
